import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { Keyboard, Languages, Zap, Settings } from 'lucide-react';

export const KeyboardSettings: React.FC = () => {
  const [repeatDelay, setRepeatDelay] = useState([500]);
  const [repeatRate, setRepeatRate] = useState([30]);
  const [language, setLanguage] = useState('en-US');
  const [layout, setLayout] = useState('qwerty');
  const [stickyKeys, setStickyKeys] = useState(false);
  const [filterKeys, setFilterKeys] = useState(false);
  const [toggleKeys, setToggleKeys] = useState(false);
  const [soundOnCaps, setSoundOnCaps] = useState(true);
  const [testText, setTestText] = useState('');

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Keyboard Settings</h1>
          <p className="text-muted-foreground">
            Configure keyboard behavior and accessibility options
          </p>
        </div>

        {/* Character Repeat */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Keyboard size={20} />
              Character Repeat
            </CardTitle>
            <CardDescription>
              Control how fast characters repeat when keys are held down
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="repeat-delay">Repeat Delay</Label>
                <span className="text-sm text-muted-foreground">
                  {repeatDelay[0]}ms
                </span>
              </div>
              <Slider
                id="repeat-delay"
                value={repeatDelay}
                onValueChange={setRepeatDelay}
                min={250}
                max={1000}
                step={50}
                className="w-full"
              />
              <div className="text-xs text-muted-foreground">
                Long delay ← → Short delay
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="repeat-rate">Repeat Rate</Label>
                <span className="text-sm text-muted-foreground">
                  {repeatRate[0]} chars/sec
                </span>
              </div>
              <Slider
                id="repeat-rate"
                value={repeatRate}
                onValueChange={setRepeatRate}
                min={2}
                max={40}
                step={2}
                className="w-full"
              />
              <div className="text-xs text-muted-foreground">
                Slow ← → Fast
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="test-input">Test here:</Label>
              <Input
                id="test-input"
                placeholder="Click here and hold down a key to test the repeat rate"
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                className="font-mono"
              />
            </div>
          </CardContent>
        </Card>

        {/* Language and Layout */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages size={20} />
              Language & Layout
            </CardTitle>
            <CardDescription>
              Set keyboard language and layout preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en-US">English (United States)</SelectItem>
                  <SelectItem value="en-GB">English (United Kingdom)</SelectItem>
                  <SelectItem value="es-ES">Spanish (Spain)</SelectItem>
                  <SelectItem value="fr-FR">French (France)</SelectItem>
                  <SelectItem value="de-DE">German (Germany)</SelectItem>
                  <SelectItem value="ja-JP">Japanese</SelectItem>
                  <SelectItem value="zh-CN">Chinese (Simplified)</SelectItem>
                  <SelectItem value="ru-RU">Russian</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="layout">Keyboard Layout</Label>
              <Select value={layout} onValueChange={setLayout}>
                <SelectTrigger>
                  <SelectValue placeholder="Select layout" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qwerty">QWERTY</SelectItem>
                  <SelectItem value="dvorak">Dvorak</SelectItem>
                  <SelectItem value="colemak">Colemak</SelectItem>
                  <SelectItem value="azerty">AZERTY</SelectItem>
                  <SelectItem value="qwertz">QWERTZ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Accessibility Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap size={20} />
              Accessibility Options
            </CardTitle>
            <CardDescription>
              Special keyboard features for easier typing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="sticky-keys">Sticky Keys</Label>
                  <p className="text-sm text-muted-foreground">
                    Press modifier keys one at a time instead of holding them
                  </p>
                </div>
                <Switch
                  id="sticky-keys"
                  checked={stickyKeys}
                  onCheckedChange={setStickyKeys}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="filter-keys">Filter Keys</Label>
                  <p className="text-sm text-muted-foreground">
                    Ignore brief or repeated keystrokes
                  </p>
                </div>
                <Switch
                  id="filter-keys"
                  checked={filterKeys}
                  onCheckedChange={setFilterKeys}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="toggle-keys">Toggle Keys</Label>
                  <p className="text-sm text-muted-foreground">
                    Play sounds when Caps Lock, Num Lock, or Scroll Lock are pressed
                  </p>
                </div>
                <Switch
                  id="toggle-keys"
                  checked={toggleKeys}
                  onCheckedChange={setToggleKeys}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Advanced Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings size={20} />
              Advanced Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="sound-on-caps">Sound when Caps Lock is pressed</Label>
              <Switch
                id="sound-on-caps"
                checked={soundOnCaps}
                onCheckedChange={setSoundOnCaps}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="font-medium">Shortcut Keys</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Switch input language:</span>
                  <code className="bg-muted px-2 py-1 rounded">Alt + Shift</code>
                </div>
                <div className="flex justify-between">
                  <span>Toggle Sticky Keys:</span>
                  <code className="bg-muted px-2 py-1 rounded">Shift (5 times)</code>
                </div>
                <div className="flex justify-between">
                  <span>Toggle Filter Keys:</span>
                  <code className="bg-muted px-2 py-1 rounded">Right Shift (8 sec)</code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline">Reset to Defaults</Button>
          <Button>Apply Changes</Button>
        </div>
      </div>
    </div>
  );
};